/**
 * SuperAdmin Global Look & Feel Configuration Page
 * 
 * This page allows SuperAdmins to set global theming that applies to ALL users.
 * Unlike the personal theme settings (localStorage), these settings are saved
 * to the database and loaded on app startup for everyone.
 */

/* eslint-disable react/forbid-dom-props */

import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGlobalTheme, applyGlobalThemeColors } from '@/hooks/useGlobalTheme';
import { apiFetch } from '@/lib/http';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Palette, Save, RotateCcw, Info, Loader2, Type } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';

interface GlobalConfig {
  id: number;
  primaryColor: string;
  accentColor: string;
  navBgColor: string;
  navTextColor: string;
  navActiveColor: string;
  primaryHoverColor: string;
  fontFamily: string;
  logoUrl: string | null;
  updatedAt: string;
}

// Font options
const FONT_OPTIONS = [
  { value: 'inter', label: 'Inter', description: 'Modern & clean' },
  { value: 'roboto', label: 'Roboto', description: "Google's signature font" },
  { value: 'openSans', label: 'Open Sans', description: 'Friendly & readable' },
  { value: 'lato', label: 'Lato', description: 'Elegant & professional' },
  { value: 'montserrat', label: 'Montserrat', description: 'Geometric & stylish' },
  { value: 'mulish', label: 'Mulish', description: 'Minimalist & versatile' },
  { value: 'poppins', label: 'Poppins', description: 'Rounded & friendly' },
  { value: 'sourceSans', label: 'Source Sans Pro', description: "Adobe's workhorse" },
  { value: 'nunito', label: 'Nunito', description: 'Soft & approachable' },
  { value: 'raleway', label: 'Raleway', description: 'Sophisticated & thin' },
  { value: 'ubuntu', label: 'Ubuntu', description: 'Warm & humanist' },
  { value: 'system', label: 'System Default', description: 'OS native font' },
  { value: 'mono', label: 'Monospace', description: 'Code & technical' },
];

// Font family CSS mappings
const FONT_FAMILIES: Record<string, string> = {
  inter: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  roboto: "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  openSans: "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  lato: "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  montserrat: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mulish: "'Mulish', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  poppins: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  sourceSans: "'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  nunito: "'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  raleway: "'Raleway', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  ubuntu: "'Ubuntu', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  system: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  mono: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', 'Droid Sans Mono', monospace",
};

export default function SuperAdminLookAndFeelPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Load current global config
  const { data: globalConfig, isLoading, error } = useGlobalTheme();

  // Form state
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  const [accentColor, setAccentColor] = useState('#1F2937');
  const [navBgColor, setNavBgColor] = useState('#0F3A3F');
  const [navTextColor, setNavTextColor] = useState('#FFFFFF');
  const [navActiveColor, setNavActiveColor] = useState('#0F3A3F');
  const [primaryHoverColor, setPrimaryHoverColor] = useState('#00917E');
  const [fontFamily, setFontFamily] = useState('inter');
  const [logoUrl, setLogoUrl] = useState('');

  // Picker visibility
  const [showPrimaryPicker, setShowPrimaryPicker] = useState(false);
  const [showAccentPicker, setShowAccentPicker] = useState(false);
  const [showNavBgPicker, setShowNavBgPicker] = useState(false);
  const [showNavTextPicker, setShowNavTextPicker] = useState(false);
  const [showNavActivePicker, setShowNavActivePicker] = useState(false);
  const [showPrimaryHoverPicker, setShowPrimaryHoverPicker] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Initialize form from loaded config
  useEffect(() => {
    if (globalConfig) {
      setPrimaryColor(globalConfig.primaryColor || '#3B82F6');
      setAccentColor(globalConfig.accentColor || '#1F2937');
      setNavBgColor(globalConfig.navBgColor || '#0F3A3F');
      setNavTextColor(globalConfig.navTextColor || '#FFFFFF');
      setNavActiveColor(globalConfig.navActiveColor || '#0F3A3F');
      setPrimaryHoverColor(globalConfig.primaryHoverColor || '#00917E');
      setFontFamily(globalConfig.fontFamily || 'inter');
      setLogoUrl(globalConfig.logoUrl || '');
      setIsDirty(false);
    }
  }, [globalConfig]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: {
      primaryColor: string;
      accentColor: string;
      navBgColor: string;
      navTextColor: string;
      navActiveColor: string;
      primaryHoverColor: string;
      fontFamily: string;
      logoUrl: string | null;
    }) => {
      const response = await apiFetch('/api/look-and-feel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Failed to save');
      }
      return response.json();
    },
    onSuccess: (data) => {
      // Apply colors immediately
      applyGlobalThemeColors(data.primaryColor, data.accentColor);

      // Sync --primary so default buttons update immediately
      document.documentElement.style.setProperty('--primary', data.primaryColor);

      // Apply navigation bar colors
      document.documentElement.style.setProperty('--navigation-bg', data.navBgColor);
      document.documentElement.style.setProperty('--navigation-text', data.navTextColor);
      document.documentElement.style.setProperty('--nav-active', data.navActiveColor || '#0F3A3F');
      document.documentElement.style.setProperty('--primary-hover', data.primaryHoverColor || '#00917E');

      // Apply font family
      const fontStack = FONT_FAMILIES[data.fontFamily] || FONT_FAMILIES.inter;
      document.documentElement.style.setProperty('--font-family-base', fontStack);
      document.body.style.fontFamily = fontStack;

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['look-and-feel'] });
      queryClient.invalidateQueries({ queryKey: ['global-theme'] });

      setIsDirty(false);
      toast({
        title: 'Settings Saved',
        description: 'Global look and feel has been updated for all users.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Save Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSave = () => {
    saveMutation.mutate({
      primaryColor,
      accentColor,
      navBgColor,
      navTextColor,
      navActiveColor,
      primaryHoverColor,
      fontFamily,
      logoUrl: logoUrl || null,
    });
  };

  const handleReset = () => {
    if (globalConfig) {
      setPrimaryColor(globalConfig.primaryColor || '#3B82F6');
      setAccentColor(globalConfig.accentColor || '#1F2937');
      setNavBgColor(globalConfig.navBgColor || '#0F3A3F');
      setNavTextColor(globalConfig.navTextColor || '#FFFFFF');
      setNavActiveColor(globalConfig.navActiveColor || '#0F3A3F');
      setPrimaryHoverColor(globalConfig.primaryHoverColor || '#00917E');
      setFontFamily(globalConfig.fontFamily || 'inter');
      setLogoUrl(globalConfig.logoUrl || '');
      setIsDirty(false);

      // Reset live preview
      if (globalConfig.navBgColor) {
        document.documentElement.style.setProperty('--navigation-bg', globalConfig.navBgColor);
      }
      if (globalConfig.navTextColor) {
        document.documentElement.style.setProperty('--navigation-text', globalConfig.navTextColor);
      }
      document.documentElement.style.setProperty('--nav-active', globalConfig.navActiveColor || '#0F3A3F');
      document.documentElement.style.setProperty('--primary-hover', globalConfig.primaryHoverColor || '#00917E');
    }
  };

  // Live preview handlers
  const handleNavBgChange = (color: string) => {
    setNavBgColor(color);
    setIsDirty(true);
    document.documentElement.style.setProperty('--navigation-bg', color);
  };

  const handleNavTextChange = (color: string) => {
    setNavTextColor(color);
    setIsDirty(true);
    document.documentElement.style.setProperty('--navigation-text', color);
  };

  const handleFontChange = (value: string) => {
    setFontFamily(value);
    setIsDirty(true);
    const fontStack = FONT_FAMILIES[value] || FONT_FAMILIES.inter;
    document.documentElement.style.setProperty('--font-family-base', fontStack);
    document.body.style.fontFamily = fontStack;
  };

  const handlePrimaryChange = (color: string) => {
    setPrimaryColor(color);
    setIsDirty(true);
    document.documentElement.style.setProperty('--global-primary', color);
    document.documentElement.style.setProperty('--primary', color);
  };

  const handleAccentChange = (color: string) => {
    setAccentColor(color);
    setIsDirty(true);
    document.documentElement.style.setProperty('--global-accent', color);
  };

  const handleNavActiveChange = (color: string) => {
    setNavActiveColor(color);
    setIsDirty(true);
    document.documentElement.style.setProperty('--nav-active', color);
  };

  const handlePrimaryHoverChange = (color: string) => {
    setPrimaryHoverColor(color);
    setIsDirty(true);
    document.documentElement.style.setProperty('--primary-hover', color);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading settings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Error Loading Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <p>{error instanceof Error ? error.message : 'Failed to load global settings'}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Palette className="h-6 w-6" />
          Global Look & Feel
        </h1>
        <p className="text-muted-foreground mt-1">
          Configure the appearance for all users. Changes here apply globally.
        </p>
      </div>

      <Separator />

      {/* Navigation Bar Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Navigation Bar</CardTitle>
          <CardDescription>
            Customize the sidebar navigation appearance.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Nav Background Color */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Background Color</Label>
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-lg border-2 cursor-pointer transition-transform hover:scale-105"
                style={{ backgroundColor: navBgColor }}
                onClick={() => setShowNavBgPicker(!showNavBgPicker)}
              />
              <div className="flex-1">
                <Input
                  value={navBgColor.toUpperCase()}
                  onChange={(e) => handleNavBgChange(e.target.value)}
                  className="font-mono"
                />
              </div>
              <Button variant="outline" onClick={() => setShowNavBgPicker(!showNavBgPicker)}>
                {showNavBgPicker ? 'Hide' : 'Pick'}
              </Button>
            </div>
            {showNavBgPicker && (
              <div className="flex justify-center p-4 border rounded-lg bg-gray-50">
                <HexColorPicker color={navBgColor} onChange={handleNavBgChange} />
              </div>
            )}
          </div>

          {/* Nav Text Color */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Text Color</Label>
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-lg border-2 cursor-pointer transition-transform hover:scale-105"
                style={{ backgroundColor: navTextColor }}
                onClick={() => setShowNavTextPicker(!showNavTextPicker)}
              />
              <div className="flex-1">
                <Input
                  value={navTextColor.toUpperCase()}
                  onChange={(e) => handleNavTextChange(e.target.value)}
                  className="font-mono"
                />
              </div>
              <Button variant="outline" onClick={() => setShowNavTextPicker(!showNavTextPicker)}>
                {showNavTextPicker ? 'Hide' : 'Pick'}
              </Button>
            </div>
            {showNavTextPicker && (
              <div className="flex justify-center p-4 border rounded-lg bg-gray-50">
                <HexColorPicker color={navTextColor} onChange={handleNavTextChange} />
              </div>
            )}
          </div>

          {/* Active Navigation Item Color */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Active Navigation Color</Label>
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-lg border-2 cursor-pointer transition-transform hover:scale-105"
                style={{ backgroundColor: navActiveColor }}
                onClick={() => setShowNavActivePicker(!showNavActivePicker)}
              />
              <div className="flex-1">
                <Input
                  value={navActiveColor.toUpperCase()}
                  onChange={(e) => handleNavActiveChange(e.target.value)}
                  className="font-mono"
                />
              </div>
              <Button variant="outline" onClick={() => setShowNavActivePicker(!showNavActivePicker)}>
                {showNavActivePicker ? 'Hide' : 'Pick'}
              </Button>
            </div>
            {showNavActivePicker && (
              <div className="flex justify-center p-4 border rounded-lg bg-gray-50">
                <HexColorPicker color={navActiveColor} onChange={handleNavActiveChange} />
              </div>
            )}
          </div>

          {/* Navigation Preview */}
          <div className="mt-4">
            <Label className="flex items-center gap-2 mb-2">
              <Info className="h-4 w-4" />
              Preview
            </Label>
            <div
              className="p-4 rounded-lg"
              style={{
                backgroundColor: navBgColor,
                color: navTextColor
              }}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold">ReActor</span>
                <div className="flex gap-2">
                  <Badge style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: navTextColor }}>Campaigns</Badge>
                  <Badge style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: navTextColor }}>Admin</Badge>
                </div>
              </div>
              <div className="mt-3 space-y-2 text-sm">
                <div className="px-2 py-1 rounded opacity-90">• Advertisers</div>
                <div
                  className="px-2 py-1 rounded font-semibold border-r-2"
                  style={{ backgroundColor: navActiveColor, borderColor: navTextColor }}
                >
                  • Campaigns (active)
                </div>
                <div className="px-2 py-1 rounded opacity-90">• Settings</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Font Family */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Type className="h-5 w-5" />
            Font Family
          </CardTitle>
          <CardDescription>
            Choose the font used throughout the application.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={fontFamily} onValueChange={handleFontChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a font" />
            </SelectTrigger>
            <SelectContent>
              {FONT_OPTIONS.map((font) => (
                <SelectItem key={font.value} value={font.value}>
                  <div className="flex flex-col">
                    <span style={{ fontFamily: FONT_FAMILIES[font.value] }}>{font.label}</span>
                    <span className="text-xs text-muted-foreground">{font.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Font Preview */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <p
              className="text-lg"
              style={{ fontFamily: FONT_FAMILIES[fontFamily] }}
            >
              The quick brown fox jumps over the lazy dog.
            </p>
            <p
              className="text-sm mt-2 text-muted-foreground"
              style={{ fontFamily: FONT_FAMILIES[fontFamily] }}
            >
              ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Primary Brand Color */}
      <Card>
        <CardHeader>
          <CardTitle>Primary Brand Color</CardTitle>
          <CardDescription>
            Used for buttons and primary UI elements.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-lg border-2 cursor-pointer transition-transform hover:scale-105"
              style={{ backgroundColor: primaryColor }}
              onClick={() => setShowPrimaryPicker(!showPrimaryPicker)}
            />
            <div className="flex-1">
              <Input
                value={primaryColor.toUpperCase()}
                onChange={(e) => handlePrimaryChange(e.target.value)}
                className="font-mono"
              />
            </div>
            <Button variant="outline" onClick={() => setShowPrimaryPicker(!showPrimaryPicker)}>
              {showPrimaryPicker ? 'Hide' : 'Pick'}
            </Button>
          </div>
          {showPrimaryPicker && (
            <div className="flex justify-center p-4 border rounded-lg bg-gray-50">
              <HexColorPicker color={primaryColor} onChange={handlePrimaryChange} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Accent Color */}
      <Card>
        <CardHeader>
          <CardTitle>Accent Color</CardTitle>
          <CardDescription>
            Used for highlights, table headers, and secondary UI elements.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-lg border-2 cursor-pointer transition-transform hover:scale-105"
              style={{ backgroundColor: accentColor }}
              onClick={() => setShowAccentPicker(!showAccentPicker)}
            />
            <div className="flex-1">
              <Input
                value={accentColor.toUpperCase()}
                onChange={(e) => handleAccentChange(e.target.value)}
                className="font-mono"
              />
            </div>
            <Button variant="outline" onClick={() => setShowAccentPicker(!showAccentPicker)}>
              {showAccentPicker ? 'Hide' : 'Pick'}
            </Button>
          </div>
          {showAccentPicker && (
            <div className="flex justify-center p-4 border rounded-lg bg-gray-50">
              <HexColorPicker color={accentColor} onChange={handleAccentChange} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Primary Button Hover Color */}
      <Card>
        <CardHeader>
          <CardTitle>Primary Button Hover Color</CardTitle>
          <CardDescription>
            Shown when users hover over primary action buttons.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-lg border-2 cursor-pointer transition-transform hover:scale-105"
              style={{ backgroundColor: primaryHoverColor }}
              onClick={() => setShowPrimaryHoverPicker(!showPrimaryHoverPicker)}
            />
            <div className="flex-1">
              <Input
                value={primaryHoverColor.toUpperCase()}
                onChange={(e) => handlePrimaryHoverChange(e.target.value)}
                className="font-mono"
              />
            </div>
            <Button variant="outline" onClick={() => setShowPrimaryHoverPicker(!showPrimaryHoverPicker)}>
              {showPrimaryHoverPicker ? 'Hide' : 'Pick'}
            </Button>
          </div>
          {showPrimaryHoverPicker && (
            <div className="flex justify-center p-4 border rounded-lg bg-gray-50">
              <HexColorPicker color={primaryHoverColor} onChange={handlePrimaryHoverChange} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Logo URL */}
      <Card>
        <CardHeader>
          <CardTitle>Logo</CardTitle>
          <CardDescription>
            Optional: URL to your company logo (displayed in navigation).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Logo URL</Label>
            <Input
              value={logoUrl}
              onChange={(e) => { setLogoUrl(e.target.value); setIsDirty(true); }}
              placeholder="https://example.com/logo.png"
            />
          </div>
          {logoUrl && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <Label className="mb-2 block">Preview</Label>
              <img
                src={logoUrl}
                alt="Logo preview"
                className="max-h-16 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-4">
        <Button
          onClick={handleSave}
          disabled={!isDirty || saveMutation.isPending}
          className="flex-1"
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Changes
        </Button>
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={!isDirty}
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset
        </Button>
      </div>

      {/* Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex gap-2">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">How this works</p>
              <p className="text-sm text-blue-700 mt-1">
                These settings are saved to the database and apply to <strong>all users</strong> (Admins and SuperAdmins).
                Changes take effect immediately for you and on the next page load for other users.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
